import { useMergeRefs } from "@floating-ui/react"
import classNames from "classnames"
import type {
  ComponentPropsWithoutRef,
  ChangeEventHandler,
  CSSProperties,
  Ref,
} from "react"
import {
  forwardRef,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import styles from "./SliderValue.module.scss"
import { useSliderValueContext } from "./SliderValueContext"
import { UnstyledInput } from "src/inputs/UnstyledInputs"

export interface Props extends Omit<ComponentPropsWithoutRef<"input">, "value"> {
  value?: string | number;
  defaultValue?: string | number;
}

export const SliderValueInput = forwardRef<HTMLInputElement, Props>(
  function SliderValueInput(props: Props, propRef: Ref<HTMLInputElement>) {
    const internalRef = useRef<HTMLInputElement>(null)

    const ref = useMergeRefs([
      propRef,
      internalRef,
    ])

    const {
      defaultValue = "",
      min,
      max,
      onChange: controlledOnChange,
      placeholder = min !== undefined ? `${min}` : "0",
      value: controlledValue,
      ...htmlInputProps
    } = props

    const {
      unitWidth,
      hasLabel,
      setValue,
      setMax,
      setPlaceholder,
    } = useSliderValueContext()

    const [
      uncontrolledValue,
      setUncontrolledValue,
    ] = useState<string | number>(defaultValue)

    // when the uncontrolled value changes, update the context and uncontrolled value
    const onChange: ChangeEventHandler<HTMLInputElement> = useCallback(
      (e) => {
        setValue(e.target.value)
        setUncontrolledValue(e.target.value)
        controlledOnChange?.(e)
      },
      [
        setValue,
        setUncontrolledValue,
        controlledOnChange,
      ],
    )

    // when the controlledValue changes, update the context
    useLayoutEffect(
      () => {
        if (controlledValue !== undefined) {
          setValue(controlledValue)

          return () => setValue(defaultValue)
        }
      },
      [
        controlledValue,
        defaultValue,
        setValue,
      ],
    )

    // when the defaultValue changes, update the context and uncontrolled value
    useLayoutEffect(
      () => {
        if (controlledValue === undefined && uncontrolledValue === "") {
          setValue(defaultValue)
          setUncontrolledValue(defaultValue)
        }
      },
      [
        defaultValue,
        setUncontrolledValue,
        setValue,
        controlledValue,
        uncontrolledValue,
      ],
    )

    // when the component first mounts, update the input values
    useLayoutEffect(
      () => {
        const currentValue = internalRef.current?.value

        if (currentValue) {
          setValue(currentValue)
        }
      },
      [setValue],
    )

    // when the max changes, update the context
    useLayoutEffect(
      () => {
        if (max !== undefined) {
          setMax(max)
        }
      },
      [
        max,
        setMax,
      ],
    )

    // when the placeholder changes, update the context
    useLayoutEffect(
      () => {
        if (placeholder !== undefined) {
          setPlaceholder(placeholder)
        }
      },
      [
        placeholder,
        setPlaceholder,
      ],
    )

    const className = classNames(
      props.className,
      styles.sliderValueInput,
      hasLabel && styles.sliderValueInputWithLabel,
    )

    const style: CSSProperties = useMemo(
      () => {
        if (unitWidth) {
          return {
            paddingInlineEnd: unitWidth,
          }
        }

        return {}
      },
      [unitWidth],
    )

    const value = controlledValue === undefined ?
      uncontrolledValue :
      `${controlledValue}`

    return (
      <UnstyledInput
        type="number"
        {...htmlInputProps}
        className={className}
        onChange={onChange}
        ref={ref}
        style={style}
        value={value}
        min={min}
        max={max}
        placeholder={placeholder}
      />
    )
  },
)
